import React, { useState, useEffect } from 'react';
import { Download, AlertTriangle, Clock } from 'lucide-react';
import api from '../../services/api';
import ReportFilters, { getDefaultDateRange } from './ReportFilters';

const OutstandingPaymentsReport = () => {
  const [data, setData] = useState({ results: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    ...getDefaultDateRange(),
    supplier: '',
    cost_centre: '',
  });

  const fetchData = async (appliedFilters = filters) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (appliedFilters.date_from) params.append('date_from', appliedFilters.date_from);
      if (appliedFilters.date_to) params.append('date_to', appliedFilters.date_to);
      if (appliedFilters.supplier) params.append('supplier', appliedFilters.supplier);
      if (appliedFilters.cost_centre) params.append('cost_centre', appliedFilters.cost_centre);
      
      const response = await api.get(`/reports/outstanding/?${params.toString()}`);
      const responseData = response.data;
      setData({
        results: Array.isArray(responseData) ? responseData : (responseData.results || []),
        totals: responseData.totals || {}
      });
    } catch (err) {
      setError('Failed to load report data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchData(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      ...getDefaultDateRange(),
      supplier: '',
      cost_centre: '',
    };
    setFilters(defaultFilters);
    fetchData(defaultFilters);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.supplier) params.append('supplier', filters.supplier);
      if (filters.cost_centre) params.append('cost_centre', filters.cost_centre);
      
      const response = await api.get(`/reports/outstanding/export/?${params.toString()}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `outstanding_payments_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report');
    }
  };

  const formatCurrency = (amount) => {
    return `R ${parseFloat(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA');
  };

  // Calculate days overdue (if payment date is in the past)
  const getDaysOverdue = (paymentDate) => {
    if (!paymentDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const payment = new Date(paymentDate);
    payment.setHours(0, 0, 0, 0);
    const diffTime = today - payment;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : null;
  };

  return (
    <div className="report-container">
      <div className="report-header">
        <h1>Outstanding Payments Report</h1>
        <button className="btn btn-primary" onClick={handleExport} disabled={loading}>
          <Download size={18} />
          Export to Excel
        </button>
      </div>

      <div className="card">
        <ReportFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          showPaidStatus={false}  // This report only shows unpaid
        />
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        {/* Summary Stats */}
        <div className="report-stats">
          <div className="stat-card highlight-warning">
            <div className="stat-label">Outstanding Amount</div>
            <div className="stat-value">{formatCurrency(data.totals?.grand_total)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Outstanding Payments</div>
            <div className="stat-value">{data.totals?.total_payments || 0}</div>
          </div>
        </div>

        {/* Alert if there are outstanding payments */}
        {!loading && data.results.length > 0 && (
          <div className="outstanding-alert">
            <AlertTriangle size={20} />
            <span>
              {data.totals?.total_payments} payment{data.totals?.total_payments !== 1 ? 's' : ''} totalling{' '}
              <strong>{formatCurrency(data.totals?.grand_total)}</strong> {data.totals?.total_payments !== 1 ? 'are' : 'is'} awaiting payment confirmation.
            </span>
          </div>
        )}

        {/* Data Table */}
        {loading ? (
          <div className="loading">Loading report data...</div>
        ) : data.results.length === 0 ? (
          <div className="empty-state success-state">
            <div className="success-icon">✓</div>
            <h3>All Caught Up!</h3>
            <p>No outstanding payments found for the selected filters.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Payment Date</th>
                  <th>Supplier</th>
                  <th className="text-right">Amount</th>
                  <th>Reference</th>
                  <th>Batch</th>
                  <th>Cost Centre</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((row) => {
                  const daysOverdue = getDaysOverdue(row.payment_date);
                  return (
                    <tr key={row.id} className={daysOverdue ? 'row-overdue' : ''}>
                      <td>
                        <div className="date-cell">
                          {formatDate(row.payment_date)}
                          {daysOverdue && (
                            <span className="overdue-badge" title={`${daysOverdue} days overdue`}>
                              {daysOverdue}d overdue
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{row.cr_account_name}</td>
                      <td className="text-right"><strong>{formatCurrency(row.amount)}</strong></td>
                      <td className="text-muted">{row.cr_statement_ref}</td>
                      <td>
                        <span className="batch-ref">{row.batch_reference}</span>
                      </td>
                      <td>
                        {row.cost_centre_code ? (
                          <span className="code-badge">{row.cost_centre_code}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="badge badge-warning">
                          <Clock size={12} /> Pending
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total Outstanding</strong></td>
                  <td><strong>{data.totals?.total_payments} payments</strong></td>
                  <td className="text-right"><strong>{formatCurrency(data.totals?.grand_total)}</strong></td>
                  <td colSpan="4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutstandingPaymentsReport;